<script setup lang="ts">
import { computed, onMounted, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useRoute } from "vue-router"

import Card from "@/components/shared/Card.vue"
import CardContent from "@/components/shared/CardContent.vue"
import CardSubtitle from "@/components/shared/CardSubtitle.vue"
import CardTitle from "@/components/shared/CardTitle.vue"
import InfoRow from "@/components/shared/InfoRow.vue"
import InfoTable from "@/components/shared/InfoTable.vue"
import LayoutAuthed from "@/components/shared/LayoutAuthed.vue"
import StatusBadge from "@/components/shared/StatusBadge.vue"
import Table from "@/components/shared/Table.vue"
import router from "@/plugins/router"
import settlementService from "@/services/settlement"
import type { Product } from "@/types/product.type"
import type { SettlementDetail } from "@/types/settlement.type"
import { formattedMoney } from "@/utils/money"
import { reportSentry } from "@/utils/sentry"
import { formattedTime, unitedNumber } from "@/utils/string"

interface Item {
  order: { id?: number; code: string } | null
  product: Product | null
  quantity: number | null
  supplyAmount: number
  tax: number
  amount: number
}

const route = useRoute()
const { t } = useI18n()

const payout = ref<SettlementDetail | null>(null)
const id = computed(() => route.params.id)
const pageTitle = computed(() => {
  return `${t("payout.title")} #${id.value}`
})
const items = ref<Item[]>([])

async function loadPayout() {
  const data = await settlementService.get(Number(id.value))
  payout.value = data
  items.value = payout.value.settlementItems
    .slice()
    .sort((a, b) => {
      const aHas = a.purchaseProduct || a.productCut ? 0 : 1
      const bHas = b.purchaseProduct || b.productCut ? 0 : 1
      return aHas - bHas
    })
    .map((item) => {
      const newItem: Item = { ...item, quantity: null, order: null, product: null }

      if (item.purchaseProduct) {
        newItem.order = item.purchaseProduct.purchase
        newItem.product = item.purchaseProduct.product
        newItem.quantity = item.quantity
      } else if (item.productCut) {
        newItem.order = { code: t("payout.item.stockShipOut") }
        newItem.product = item.productCut.product
        newItem.quantity = item.quantity
      }

      return newItem
    })
}

onMounted(async () => {
  try {
    await loadPayout()
  } catch (error) {
    console.error(error)
    reportSentry(error)
    router.replace("/payouts")
  }
})
</script>

<template>
  <LayoutAuthed :title="pageTitle">
    <Card v-if="payout">
      <CardTitle>{{ t("payout.detail") }}</CardTitle>
      <CardContent>
        <InfoTable>
          <InfoRow :name="$t('payout.supplyAmount')">
            {{ formattedMoney(payout.supplyAmount, payout.currency) }}
          </InfoRow>
          <InfoRow :name="$t('payout.tax')">{{ formattedMoney(payout.tax, payout.currency) }}</InfoRow>
          <InfoRow :name="$t('payout.amount')">{{ formattedMoney(payout.amount, payout.currency) }}</InfoRow>
          <InfoRow :name="$t('payout.paidTo')">
            {{ payout.bankAccount.bankName }} {{ payout.bankAccount.accountNumber }}
          </InfoRow>
          <InfoRow :name="$t('payout.completedAt')">{{ formattedTime(payout.completedAt) }}</InfoRow>
          <InfoRow :name="$t('payout.status')">
            <StatusBadge model="payout" field="status" :value="payout.status" />
          </InfoRow>
        </InfoTable>
      </CardContent>
      <CardSubtitle>{{ t("payout.contents") }} ({{ items.length }})</CardSubtitle>
      <CardContent>
        <Table>
          <template #header>
            <tr>
              <th>{{ t("payout.item.order") }}</th>
              <th>{{ t("payout.item.product") }}</th>
              <th>{{ t("payout.item.quantity") }}</th>
              <th>{{ t("payout.item.supplyAmount") }}</th>
              <th>{{ t("payout.item.tax") }}</th>
              <th>{{ t("payout.item.amount") }}</th>
            </tr>
          </template>
          <template #default>
            <tr v-for="(item, index) in items" :key="index">
              <td>
                <router-link v-if="item.order && item.order.id" :to="`/orders/${item.order.id}`">
                  {{ item.order.code }}
                </router-link>
                <template v-else-if="item.order && item.order.code">{{ item.order.code }}</template>
              </td>
              <td>
                <template v-if="item.product">
                  {{ item.product.quality.name }}
                  <template v-if="item.product.productType === 'actual_color'">
                    #{{ item.product.colorCode }}
                  </template>
                </template>
              </td>
              <td>
                <template v-if="item.product">{{ unitedNumber(item.quantity, $t("item.units.yards_short")) }}</template>
              </td>
              <td>{{ formattedMoney(item.supplyAmount, payout.currency) }}</td>
              <td>{{ formattedMoney(item.tax, payout.currency) }}</td>
              <td>{{ formattedMoney(item.amount, payout.currency) }}</td>
            </tr>
          </template>
        </Table>
      </CardContent>
    </Card>
  </LayoutAuthed>
</template>

<style scoped></style>
